import config from "config.json";
import useFetch from "hooks/useFetch";
import useLocalStorage from "hooks/useLocalStorage";
import useScript from "hooks/useScript";
import useSessionStorage from "hooks/useSessionStorage";
import { useEffect, useRef, useState } from "react";
import {
	addCurrentRedirectUrl,
	getEnvironment,
	isAllMarketingSubscribed,
	isCdnPage,
	isCurrentPathPresent,
	isDevelopmentEnvironment,
	isIsolatedEnvironment,
	isTravelAgent,
	mapString,
	mapStringWithObject,
	objectIsEmpty,
	validateJsonWebToken,
} from "utils";
import { getLocalStorageValue } from "utils/localStorage";
import { loadAffiliationsFromCookies } from "utils/affiliations";
import { getCookie } from "utils/cookie";
import { buildHref } from "utils/link";
import { endChatSession } from "utils/newChat";
import {
	getTravelAgentDataFromCookie,
	publishNavigationEvent,
	publishOneIdError,
	getNotificationsType,
	removeCartCookie,
	getNotifications,
	getOneIdConfig,
	getUniversalAuthenticatorConfig,
	oneIdPrefix,
	shouldUseUniversalAuth,
	removeOciCookies,
} from "utils/profile";

const IS_DEBUG_ENABLED = getLocalStorageValue("navDebugOneId") === "true";

const logProfileInfo = (...args) => {
	console.log("[OneID-nav]", ...args);
};

const logProfileError = (...args) => {
	if (IS_DEBUG_ENABLED) {
		console.error("[OneID-nav]", ...args);
	}
};

const isolatedEnvironment = isIsolatedEnvironment();

const maxChecks = 60;
const timeoutMs = 500;
const asyncMessagingTokenKey = "navigationAppAsyncMessagingValue";
const authenticationEvent = "navigationAuth";
const defaultUserType = "GUEST";
const launchLoginMethod = "launchLogin";
const launchIdentityFlowMethod = "launchIdentityFlow";
const loadHeavyNotificationsKey = "navigationAppLoadHeavyNotifications";
const loadHeavyNotificationsReloadTime = 1800000; // 30 minutes
const notificationsCookieName = "notificationCount";
const syndicatedDismissButton = "syndicatedDismissButton";
const syndicatedMakeCartRequest = "syndicatedMakeUserCartRequest";
const authenticatorBundlePath = "/authenticator/authenticator-bundle.js";
let navigationAllowReloadAndRedirect = false;
const localStoragePreventReloadAndRedirect = getLocalStorageValue(
	"preventPageReloadAndRedirect"
);

const useProfile = ({
	brand,
	locale,
	profile,
	store,
	affiliationsParam,
	chromeData,
	basePath,
	additionalProfileInfo,
	bannerProfile,
	disableProfileLibrary,
	isConversationStarted,
	closeChat,
	cdnUrl,
	isUniversalAuthEnabled,
}) => {
	const resetOnActionRedirection = () => {
		if (disneyIdRef.current?.onLoginRedirectTo) {
			disneyIdRef.current.onLoginRedirectTo = null;
		}
		if (disneyIdRef.current?.onLogoutRedirectTo) {
			disneyIdRef.current.onLogoutRedirectTo = null;
		}
	};

	const appEnv = isCdnPage() ? "development" : getEnvironment();
	const brandConfig = config.brands?.[brand];
	const envConfig = config?.environmentConfiguration?.[appEnv] ?? {};
	const notificationsType = getNotificationsType(brandConfig, appEnv);
	const oneIdConfig = getOneIdConfig({
		config,
		brandConfig,
		brand,
		appEnv,
		isCurrentPathPresent,
		localOneIdLibraryInstance: isolatedEnvironment ? null : undefined,
		disableProfileLibrary,
		mapString,
		mapStringWithObject,
		isDevelopmentEnvironment,
		isCdnPage,
		authenticatorBundlePath,
		getCookie,
		store,
	});
	const isOneIdSource = oneIdConfig?.isOneIdSource || false;
	const { useIdentityWebSDK } = oneIdConfig || false;

	const [user, setUser] = useState(null);
	const [profileUrl, setProfileUrl] = useState("");
	const [openedOneIdModal, setOpenedOneIdModal] = useState(false);
	const [isLoading, setIsLoading] = useState(profile && isOneIdSource);
	const [affiliations, setAffiliations] = useState([]);
	const [userType, setUserType] = useState(
		brandConfig?.isUserTypeRequiredForContentRequest &&
			window?.location.pathname.split("/").includes("trade-public")
			? "TRAVEL_AGENT_NOT_SIGNED_IN"
			: null
	);
	const [travelAgent, setTravelAgent] = useState(null);
	const [showSubscriptionMessage, setShowSubscriptionMessage] = useState(false);
	const [, setStorageMakeCartRequest] = useSessionStorage(
		syndicatedMakeCartRequest,
		true
	);
	const [, setSyndicatedDismissButton] = useLocalStorage(
		syndicatedDismissButton,
		false
	);
	const [asyncMessagingToken, setAsyncMessagingToken] = useSessionStorage(
		asyncMessagingTokenKey,
		null
	);
	const [loadHeavyNotificationsObject, setLoadHeavyNotificationsObject] =
		useSessionStorage(loadHeavyNotificationsKey, null);
	const [, setChatConfigurationValue] = useSessionStorage(
		config.chatConfigurationKey,
		""
	);

	const disneyIdRef = useRef();
	const initOneIdCalledRef = useRef(false);
	const isConversationStartedRef = useRef(isConversationStarted);
	const closeChatRef = useRef(closeChat);
	const useUniversalAuthFlag = getLocalStorageValue("useUniversalAuth");
	const isUniversalAuthEnabledFlag = getLocalStorageValue(
		"isUniversalAuthEnabled"
	);
	const useUniversalAuth = shouldUseUniversalAuth({
		locale,
		useUniversalAuthFlag,
		brand,
		envConfig,
		includedPages:
			oneIdConfig?.forceScriptLoadAndInitializationPages,
	});
	const universalAuthEnabled =
		isUniversalAuthEnabledFlag !== null
			? isUniversalAuthEnabledFlag === "true"
			: isUniversalAuthEnabled ?? false;

	const status = useScript(
		disableProfileLibrary !== undefined &&
			!disableProfileLibrary &&
			profile &&
			isOneIdSource &&
			!oneIdConfig?.localOneIdLibraryInstance &&
			isUniversalAuthEnabled !== undefined &&
			!isUniversalAuthEnabled &&
			!universalAuthEnabled &&
			!useUniversalAuth
			? oneIdConfig?.disneyIDUrl
			: null,
		"head"
	);
	const [notificationCount, setNotificationCount] = useState(
		getCookie(notificationsCookieName)
	);
	const [triggerNotificationCount, setTriggerNotificationCount] = useState(0);

	const { response, error } = useFetch(
		profileUrl,
		{
			headers: { swid: user?.swid || "", token: user?.authToken || "" },
		},
		0,
		profileUrl
	);

	const setNewAffiliations = (newAffiliations = []) => {
		setAffiliations((currentAffiliations = []) => {
			return Array.from(new Set([...currentAffiliations, ...newAffiliations]));
		});
	};

	const readDataFromCookie = () => {
		const travelAgentData = getTravelAgentDataFromCookie();
		if (travelAgentData) {
			setUserType("TRAVEL_AGENT");
			setTravelAgent(travelAgentData);
		} else if (!userType && brandConfig?.isUserTypeRequiredForContentRequest) {
			setUserType(defaultUserType);
		}
	};

	useEffect(() => {
		setNewAffiliations(loadAffiliationsFromCookies(brand));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		// Check if universalAuth is being initialized from outside navigation-ui
		// when isUniversalAuthEnabled param is send as true
		if (isUniversalAuthEnabled === undefined) return;
		if (!universalAuthEnabled) {
			logProfileInfo(
				"universalAuthEnabled is false, skipping external init check"
			);
			return;
		}
		logProfileInfo("Starting external universalAuth initialization check");
		let checkForOneId;

		const checkLimit = setTimeout(() => {
			logProfileError(
				"Timeout reached waiting for external universalAuth initialization"
			);
			clearInterval(checkForOneId);
		}, timeoutMs * maxChecks);

		checkForOneId = setInterval(async () => {
			const isInitialized = window.PEP?.PROFILE_AUTH?.isInitialized ?? false;
			if (isInitialized) {
				logProfileInfo("External universalAuth detected and initialized");
				disneyIdRef.current = window.PEP?.PROFILE_AUTH;
				postInitializeOneId();
				clearInterval(checkForOneId);
				clearTimeout(checkLimit);
			}
		}, timeoutMs);

		return () => {
			clearInterval(checkForOneId);
			clearTimeout(checkLimit);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [universalAuthEnabled, isUniversalAuthEnabled]);

	useEffect(() => {
		if (affiliationsParam?.length) {
			setNewAffiliations(affiliationsParam);
		}
	}, [affiliationsParam]);

	useEffect(() => {
		if (isUniversalAuthEnabled === undefined) return;
		logProfileInfo("Checking conditions to initialize OneID", {
			universalAuthEnabled,
			useUniversalAuth,
			profile,
			isOneIdSource,
			status,
			localOneIdLibraryInstance: oneIdConfig?.localOneIdLibraryInstance,
			useIdentityWebSDK: oneIdConfig?.useIdentityWebSDK,
		});
		if (useIdentityWebSDK && profile && !disneyIdRef.current) {
			logProfileInfo("V5 SDK path detected");
			initOneId();
		} else if (
			(!universalAuthEnabled && useUniversalAuth) ||
			(profile &&
				isOneIdSource &&
				!disneyIdRef.current &&
				(status === "ready" || oneIdConfig?.localOneIdLibraryInstance) &&
				!universalAuthEnabled)
		) {
			logProfileInfo("Conditions met to initialize OneID", {
				universalAuthEnabled,
				useUniversalAuth,
				status,
				hasLocalInstance: !!oneIdConfig?.localOneIdLibraryInstance,
			});
			initOneId();
		}
		if (status === "error") {
			logProfileError("Script loading error", {
				url: oneIdConfig?.disneyIDUrl,
			});
			publishOneIdError({ message: `Unable to load script url` });
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		universalAuthEnabled,
		useUniversalAuth,
		status,
		oneIdConfig?.localOneIdLibraryInstance,
		isUniversalAuthEnabled,
	]);

	useEffect(() => {
		if (response) {
			!userType && setUserType(defaultUserType);
			const affiliationsCookieKey =
				config.cookies.affiliations[config.cookies.affiliations.length - 1];
			const geolocationCookieKey =
				config.cookies.geolocation[config.cookies.geolocation.length - 1];
			if (response[affiliationsCookieKey] || response[geolocationCookieKey]) {
				setNewAffiliations(
					loadAffiliationsFromCookies(
						brand,
						response[affiliationsCookieKey],
						response[geolocationCookieKey]
					)
				);
			}

			if (!objectIsEmpty(response.user) && response.user.firstName) {
				const firstName = response.user.firstName;
				const lastName = response.user.lastName;
				const email = response.user.email;
				const membership = response.user.membership;
				const cookieBasedNotifications =
					notificationsType !== "count" &&
					!isNaN(notificationCount) &&
					notificationCount > 0;
				if (window) {
					window.navigationGuestIdentifiers =
						response.user?.guestIdentifiers || null;
					window.document.dispatchEvent(
						new CustomEvent("navigationGuestIdentifiers", {
							detail: window?.navigationGuestIdentifiers,
						})
					);
				}
				if (response.user?.affiliations?.length) {
					setNewAffiliations(response.user.affiliations);
				}
				if (response.user.asyncMessagingToken) {
					setAsyncMessagingToken(response.user.asyncMessagingToken);
				}
				if (notificationsType === "heavy") {
					if (profileUrl.includes("loadHeavyNotifications=true")) {
						if (response.user?.notificationsResponse && !notificationCount) {
							const notificationObjectToStore = {
								messages: getNotifications(
									response.user.notificationsResponse,
									false,
									loadHeavyNotificationsObject
								),
								itemCount: response.user.notificationsResponse?.itemCount,
								updatedAt: new Date().getTime(),
								outdated: false,
							};
							setLoadHeavyNotificationsObject(notificationObjectToStore);
							setNotificationCount(
								response.user?.notificationsResponse?.itemCount
							);
						} else if (loadHeavyNotificationsObject) {
							setNotificationCount(loadHeavyNotificationsObject.itemCount);
						}
					}
				}

				setUser({
					...user,
					firstName,
					lastName,
					email: user?.email || response.user.email,
					avatar: response.user.avatar,
					affiliations: response.user.affiliations,
					messages: getNotifications(
						response.user.notificationsResponse,
						true,
						loadHeavyNotificationsObject
					),
					ebsMessageBanner: response.user.messageBanner,
					membership,
					memberClubId: response.user.memberClubId,
					cookieBasedNotifications,
					registrationDate:
						response.user.profileAggregatedResponse?.registrationDate,
					authenticatedChatToken:
						response.user.asyncMessagingToken || asyncMessagingToken,
					isDataLoaded: true,
				});
				publishNavigationEvent("userInformation", {
					swid: user?.swid,
					firstName,
					lastName,
					email,
					telephone: response.user.telephone || "",
					title: response.user.title || "",
					country: response.user.country || user?.region || "",
					affiliations: response.user.affiliations,
				});
			} else {
				setUser(null);
			}
			if (response.travelAgent?.travelAgency) {
				setTravelAgent(response.travelAgent);
				response.travelAgent?.userType &&
					setUserType(response.travelAgent.userType);
			} else {
				readDataFromCookie();
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [response]);

	useEffect(() => {
		if (error) {
			if (!userType) {
				setUserType(defaultUserType);
			}
			if (user) {
				setUser({
					...user,
					isDataLoaded: true,
					avatar: {
						...user?.avatar,
						url: `${cdnUrl}${config.profile.defaultAvatarUrl}`,
					},
				});
			}
			readDataFromCookie();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [error]);

	useEffect(() => {
		const exportAuthLogic = (event) => {
			resetOnActionRedirection();
			const {
				method,
				onLoginRedirectTo = null,
				onLogoutRedirectTo = null,
				preventReloadAndRedirect = false,
				preventExecuteOneIdMethod = false,
				forceEnablePageReloadAndRedirect = undefined,
			} = event.detail;
			logProfileInfo("AuthLogicEvent:", method);
			switch (method) {
				case launchLoginMethod:
				case launchIdentityFlowMethod:
					handleLogin(
						event,
						onLoginRedirectTo,
						onLogoutRedirectTo,
						preventReloadAndRedirect,
						preventExecuteOneIdMethod,
						forceEnablePageReloadAndRedirect
					);
					break;
				case "launchReauth":
					if (isOneIdSource) {
						handleClick(
							method,
							onLoginRedirectTo,
							onLogoutRedirectTo,
							preventReloadAndRedirect,
							preventExecuteOneIdMethod
						)(event);
					}
					break;
				case "launchProfile":
					handleProfile(event, onLoginRedirectTo, onLogoutRedirectTo, true);
					break;
				case "handleReadNotification":
					handleReadNotification();
					break;
				case "logout":
					handleLogout(
						event,
						onLogoutRedirectTo,
						true,
						preventReloadAndRedirect,
						preventExecuteOneIdMethod,
						forceEnablePageReloadAndRedirect
					);
					break;
				case "setLoggedInStatus":
					if (onLogoutRedirectTo) {
						if (!disneyIdRef.current) {
							disneyIdRef.current = {};
						}
						disneyIdRef.current.onLogoutRedirectTo = onLogoutRedirectTo;
					}
					break;
				case "handleSubscription":
					handleSubscription(event);
					break;
				default:
					if (isOneIdSource) {
						handleClick(method)(event);
					}
			}
		};

		document?.addEventListener(authenticationEvent, exportAuthLogic);

		if (!isLoading && chromeData) {
			logProfileInfo(`${authenticationEvent}Listening`);
			publishNavigationEvent(`${authenticationEvent}Listening`);
		}

		return () =>
			document?.removeEventListener(authenticationEvent, exportAuthLogic);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isLoading, chromeData, user]);

	useEffect(() => {
		const travelAgentHeader = getCookie("SHARED_SESSION_LINK");
		const makeProfileRequestForGettingTAData = travelAgentHeader && store;
		if (
			(user?.swid ||
				brandConfig?.makeProfileRequest ||
				makeProfileRequestForGettingTAData) &&
			!brandConfig?.preventProfileRequest
		) {
			const apiUrl = config.profile.apiUrl;
			const apiUrlParams = [];
			if (user?.avatar?.id) {
				apiUrlParams.push(`avatarId=${user.avatar.id}`);
			}
			if (user?.swid) {
				apiUrlParams.push("isUser=true");
			}
			if (makeProfileRequestForGettingTAData) {
				apiUrlParams.push(
					`travelAgentHeader=${encodeURIComponent(travelAgentHeader) || ""}`
				);
				apiUrlParams.push(`store=${store}`);
			}
			if (additionalProfileInfo) {
				apiUrlParams.push("additionalProfileInfo=true");
			}
			if (oneIdConfig?.asyncToken) {
				let generateToken = true;
				if (asyncMessagingToken) {
					const validToken = validateJsonWebToken(asyncMessagingToken);
					if (validToken && validToken.sub === user?.swid) {
						generateToken = false;
					}
				}
				if (generateToken) {
					setAsyncMessagingToken(null);
					apiUrlParams.push("asyncToken=true");
				}
			}
			if (notificationsType === "count") {
				apiUrlParams.push("useNotificationsCount=true");
			} else if (notificationsType === "heavy") {
				try {
					if (
						!loadHeavyNotificationsObject?.updatedAt ||
						loadHeavyNotificationsObject?.outdated ||
						new Date().getTime() - loadHeavyNotificationsObject?.updatedAt >
							loadHeavyNotificationsReloadTime
					) {
						setLoadHeavyNotificationsObject(null);
						apiUrlParams.push("loadHeavyNotifications=true");
					}
				} catch (error) {
					setLoadHeavyNotificationsObject(null);
					console.error(error);
				}
			}
			setProfileUrl(
				apiUrlParams.length ? `${apiUrl}?${apiUrlParams.join("&")}` : apiUrl
			);
		} else if (!user?.swid) {
			setProfileUrl("");
		}
		if (!makeProfileRequestForGettingTAData) {
			readDataFromCookie();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [user?.swid, brand, affiliationsParam]);

	useEffect(() => {
		isConversationStartedRef.current = isConversationStarted;
		closeChatRef.current = closeChat;
	}, [closeChat, isConversationStarted]);

	const loadCurrentUser = async () => {
		try {
			logProfileInfo("Checking logged in status", { useIdentityWebSDK });
			const userIsLoggedIn = useIdentityWebSDK
				? await disneyIdRef.current.isLoggedIn()
				: await disneyIdRef.current.getLoggedInStatus();
			logProfileInfo("User logged in status:", userIsLoggedIn);
			if (userIsLoggedIn) {
				logProfileInfo("Fetching guest data", { useIdentityWebSDK });
				const userData = useIdentityWebSDK
					? (await disneyIdRef.current.getGuest())?.data?.oneIdGuest
					: await disneyIdRef.current.getGuest();
				logProfileInfo("Guest data received", {
					hasProfile: !!userData?.profile,
					hasMarketing: !!userData?.marketing,
				});
				const authToken = useIdentityWebSDK
					? await disneyIdRef.current.getToken()
					: userData?.token;
				logProfileInfo("Auth token retrieved", { hasToken: !!authToken });
				const subscribed = isAllMarketingSubscribed(
					userData?.marketing,
					oneIdConfig?.marketingCodes
				);
				if (userData?.profile) {
					logProfileInfo("User data loaded", {
						swid: userData.profile.swid,
						email: userData.profile.email,
					});
					setUser({
						...user,
						...userData.profile,
						marketing: userData.marketing,
						authToken: authToken?.access_token,
						subscribed,
					});
				} else {
					logProfileError("User data has no profile");
				}
			}
		} catch (error) {
			logProfileError("Error loading current user", error);
			console.error(error);
		}
	};

	const initializeProfileAuth = async () => {
		if (!window?.PEP.PROFILE_AUTH.did) {
			logProfileInfo("Initializing Profile Auth with config", {
				clientId: oneIdConfig?.oneIdClientId,
				locale,
			});
			const init = await window?.PEP.PROFILE_AUTH.init({
				oneIdScriptSrc: oneIdConfig?.oneIdLibraryUrl,
				loadScript: true,
				clientId: oneIdConfig?.oneIdClientId,
				langPref: locale,
				responderPage: oneIdConfig?.oneIdResponderPage,
				cssOverride: oneIdConfig?.cssOverride,
			});
			if (!init) {
				logProfileError("Profile Auth init returned false");
				publishOneIdError({
					message: "One Id init event didn't return a true value",
				});
			} else {
				logProfileInfo("Profile Auth initialized successfully");
			}
		} else {
			logProfileInfo("Profile Auth already has did, skipping init");
		}
		disneyIdRef.current = window?.PEP.PROFILE_AUTH;
	};

	const initializeUniversalAuth = async () => {
		try {
			const { ClientIntegration } = await import(
				"@wdpr/profile-authenticator-universal"
			);
			logProfileInfo("Getting Universal Auth config", {
				brand,
				appEnv,
			});
			const authConfig = getUniversalAuthenticatorConfig(brand, appEnv);
			logProfileInfo("Running Universal Auth AppInit");
			await ClientIntegration.AppInit.runNow(authConfig);
			disneyIdRef.current = window?.PEP.PROFILE_AUTH;
			logProfileInfo("Universal Auth initialized successfully");
		} catch (e) {
			logProfileError("Universal Auth initialization failed", e);
			publishOneIdError({
				message: "One Id init event didn't return a true value",
			});
			console.error("Authenticator Universal - Initialization error", e);
		}
	};

	const checkWindowDid = async () => {
		return new Promise((resolve) => {
			let checks = 0;
			const checkOneID = () => {
				if (window?.did) {
					disneyIdRef.current = window?.did;
					resolve();
				} else if (checks >= maxChecks) {
					resolve();
				} else {
					checks++;
					setTimeout(checkOneID, timeoutMs);
				}
			};
			checkOneID();
		});
	};

	const checkWindowIdentityWebSDK = async () => {
		if (window?.WebSDK?.initialized) {
			logProfileInfo("IdentityWebSDK initialized on window");
			disneyIdRef.current = window.WebSDK;
			return;
		}
		logProfileInfo("Waiting for IdentityWebSDK on window");
		return new Promise((resolve) => {
			let checks = 0;
			const checkSDK = () => {
				if (window?.WebSDK?.initialized) {
					logProfileInfo("IdentityWebSDK initialized on window");
					disneyIdRef.current = window.WebSDK;
					resolve();
				} else if (checks >= maxChecks) {
					logProfileError("IdentityWebSDK not found after polling");
					resolve();
				} else {
					checks++;
					setTimeout(checkSDK, timeoutMs);
				}
			};
			checkSDK();
		});
	};

	const checkWindowOneID = async () => {
		return new Promise((resolve) => {
			let checks = 0;
			const checkOneID = () => {
				try {
					const oneID = window?.OneID?.get();
					if (oneID) {
						disneyIdRef.current = oneID;
						resolve();
					} else if (checks >= maxChecks) {
						resolve();
					} else {
						checks++;
						setTimeout(checkOneID, timeoutMs);
					}
				} catch (error) {
					checks++;
					setTimeout(checkOneID, timeoutMs);
				}
			};
			checkOneID();
		});
	};

	const checkOneIdCustomObject = async () => {
		if (oneIdConfig.checkForOneIdCustomObject) {
			await checkWindowDid();
		} else {
			await checkWindowOneID();
		}
	};

	const retryInitializeOneID = async () => {
		let initializeCurrentRef = true;
		if (!window?.OneID) {
			await new Promise((resolve) => {
				let checks = 0;
				const checkOneIDScript = () => {
					if (window?.OneID?.get) {
						disneyIdRef.current = window?.OneID.get();
						if (disneyIdRef.current) {
							initializeCurrentRef = false;
						}
						resolve();
					} else if (checks >= maxChecks) {
						resolve();
					} else {
						checks++;
						setTimeout(checkOneIDScript, timeoutMs);
					}
				};
				checkOneIDScript();
			});
		}

		if (initializeCurrentRef) {
			disneyIdRef.current = window?.OneID?.get({
				clientId: oneIdConfig?.oneIdClientId,
				responderPage: oneIdConfig?.oneIdResponderPage,
				cssOverride: oneIdConfig?.cssOverride,
			});
			await disneyIdRef?.current?.init();
		}
	};

	const initOneId = async () => {
		try {
			if (initOneIdCalledRef.current) {
				logProfileInfo("initOneId already called, skipping");
				return;
			}
			logProfileInfo("Starting initOneId", {
				useUniversalAuth,
				usingProfileLibrary: oneIdConfig?.usingOneIdProfileLibrary,
			});
			initOneIdCalledRef.current = true;
			if (useIdentityWebSDK) {
				logProfileInfo("Using Identity Web SDK V5");
				await checkWindowIdentityWebSDK();
			} else if (useUniversalAuth) {
				logProfileInfo("Initializing Universal Auth");
				await initializeUniversalAuth();
			} else {
				if (
					oneIdConfig?.usingOneIdProfileLibrary &&
					window?.PEP?.PROFILE_AUTH
				) {
					logProfileInfo("Initializing Profile Auth Library");
					await initializeProfileAuth();
				} else if (window?.OneID || oneIdConfig?.localOneIdLibraryInstance) {
					logProfileInfo("OneID library detected", {
						hasWindowOneID: !!window?.OneID,
						hasLocalInstance: !!oneIdConfig?.localOneIdLibraryInstance,
					});
					if (
						!oneIdConfig.forceScriptLoadAndInitialization &&
						oneIdConfig.preventInitialization &&
						!isolatedEnvironment
					) {
						logProfileInfo("Checking for custom OneID object");
						await checkOneIdCustomObject();
					}
					if (!disneyIdRef.current) {
						logProfileInfo("Retrying OneID initialization");
						await retryInitializeOneID();
					}
				} else {
					logProfileError("No OneID library found");
				}
			}
			await postInitializeOneId();
		} catch (error) {
			logProfileError("Error during initialization", error);
			publishOneIdError(error);
			console.error(error);
			setIsLoading(false);
		}
	};

	const postInitializeOneId = async () => {
		if (disneyIdRef.current) {
			logProfileInfo("Post-initialization started", {
				isInitialized: disneyIdRef.current?.isInitialized,
			});
			if (!disneyIdRef.current?.isInitialized) {
				logProfileError("OneID object exists but isInitialized is false");
				publishOneIdError({
					message: "One Id didn't initialize",
				});
			}
			logProfileInfo("Loading current user");
			await loadCurrentUser();
			logProfileInfo("Initializing OneID events");
			initOneIdEvents();
			publishNavigationEvent(`${oneIdPrefix}init`);
			const preventReloadAndRedirect =
				localStoragePreventReloadAndRedirect !== null
					? localStoragePreventReloadAndRedirect === "true"
					: oneIdConfig.preventPageReloadAndRedirect;
			disneyIdRef.current.logoutPreventReloadAndRedirect =
				disneyIdRef.current.logoutPreventReloadAndRedirect ||
				preventReloadAndRedirect;
			logProfileInfo("Post-initialization complete");
		} else {
			logProfileError(
				"disneyIdRef.current is null/undefined after initialization"
			);
			publishOneIdError({
				message: "One Id object is not defined on window",
			});
			console.error("Navigation App couldn't initialize One Id events");
		}
		logProfileInfo("Initialization complete, setting isLoading to false");
		setIsLoading(false);
	};

	const handleRedirectAndReloadLogic = (redirectKey = "Login") => {
		const redirectUrl =
			disneyIdRef.current[`on${redirectKey}RedirectTo`] ||
			disneyIdRef.current[`on${redirectKey}RedirectUrl`];
		if (!disneyIdRef.current.forceEnablePageReloadAndRedirect) {
			if (
				redirectKey === "Logout" &&
				localStoragePreventReloadAndRedirect === "false"
			) {
				logProfileInfo("Page reload");
				window?.location.reload();
				return;
			}
		}
		if (redirectUrl) {
			logProfileInfo("Page assign");
			window?.location.assign(redirectUrl);
		} else {
			logProfileInfo("Page reload");
			window?.location.reload();
		}
	};

	const initOneIdEvents = () => {
		disneyIdRef.current.on("login", async (data) => {
			setLoadHeavyNotificationsObject(null);
			setAsyncMessagingToken(null);
			navigationAllowReloadAndRedirect = true;
			logProfileInfo(`${oneIdPrefix}login`, data);
			publishNavigationEvent(`${oneIdPrefix}login`, data);
			const pathPreventReload = isCurrentPathPresent(
				brandConfig?.excludeReloadPages
			);
			if (
				localStoragePreventReloadAndRedirect === "false" ||
				disneyIdRef.current.forceEnablePageReloadAndRedirect
			) {
				handleRedirectAndReloadLogic();
			} else if (
				disneyIdRef.current.launchLoginPreventReloadAndRedirect ||
				disneyIdRef.current.launchReauthPreventReloadAndRedirect ||
				oneIdConfig.usingOneIdLibrary ||
				pathPreventReload
			) {
				navigationAllowReloadAndRedirect = false;
				const guestData = useIdentityWebSDK ? data?.oneIdGuest : data;
				logProfileInfo("Login event guest data", {
					useIdentityWebSDK,
					hasProfile: !!guestData?.profile,
					hasMarketing: !!guestData?.marketing,
				});
				const subscribed = isAllMarketingSubscribed(
					guestData?.marketing,
					oneIdConfig?.marketingCodes
				);

				guestData?.profile &&
					setUser({
						...guestData.profile,
						marketing: guestData.marketing,
						authToken: useIdentityWebSDK
							? (await disneyIdRef.current.getToken())?.access_token
							: guestData?.token?.access_token,
						subscribed,
					});
				logProfileInfo(`${oneIdPrefix}init`);
				publishNavigationEvent(`${oneIdPrefix}init`);
				if (disneyIdRef.current.leadGenLogin) {
					disneyIdRef.current.leadGenLoggedIn = true;
					handleSubscription();
				}
			} else if (
				isolatedEnvironment ||
				(navigationAllowReloadAndRedirect && oneIdConfig?.ignoreInitEvent)
			) {
				navigationAllowReloadAndRedirect = false;
				handleRedirectAndReloadLogic();
			}
		});

		disneyIdRef.current.on("session-init", () => {
			const pathPreventReload = isCurrentPathPresent(
				brandConfig?.excludeReloadPages
			);
			logProfileInfo(`${oneIdPrefix}session-init`);
			if (
				navigationAllowReloadAndRedirect &&
				!pathPreventReload &&
				!disneyIdRef.current.launchLoginPreventReloadAndRedirect &&
				!disneyIdRef.current.launchReauthPreventReloadAndRedirect
			) {
				handleRedirectAndReloadLogic();
			}
		});

		disneyIdRef.current.on("logout", async (data) => {
			logProfileInfo("Logout event received", { useIdentityWebSDK });
			setAsyncMessagingToken(null);
			setChatConfigurationValue("");
			setUser(null);
			setLoadHeavyNotificationsObject(null);
			if (isConversationStartedRef.current) {
				await closeChatRef?.current();
			} else {
				await endChatSession(brandConfig.chatConfiguration?.authenticatedChat);
			}
			if (
				!disneyIdRef.current.logoutPreventReloadAndRedirect &&
				(!oneIdConfig.checkForAllowReloadAndRedirectInLogout ||
					(oneIdConfig.checkForAllowReloadAndRedirectInLogout &&
						navigationAllowReloadAndRedirect))
			) {
				handleRedirectAndReloadLogic("Logout");
			}
			logProfileInfo(`${oneIdPrefix}logout`);
			publishNavigationEvent(`${oneIdPrefix}logout`);
		});

		disneyIdRef.current.on("update", (data) => {
			const guestData = useIdentityWebSDK ? data?.oneIdGuest : data;
			logProfileInfo("Update event", {
				useIdentityWebSDK,
				hasProfile: !!guestData?.profile,
			});
			const subscribed = isAllMarketingSubscribed(
				guestData?.marketing,
				oneIdConfig?.marketingCodes
			);
			guestData?.profile &&
				setUser({
					...user,
					...guestData.profile,
					marketing: guestData.marketing,
					subscribed,
				});
			publishNavigationEvent(`${oneIdPrefix}update`);
		});

		disneyIdRef.current.on("close", () => {
			setOpenedOneIdModal(false);
			logProfileInfo(`${oneIdPrefix}close`);
			publishNavigationEvent(`${oneIdPrefix}close`);
		});

		disneyIdRef.current.on("opt-in", () => {
			setShowSubscriptionMessage(true);
			logProfileInfo(`${oneIdPrefix}opt-in`);
			publishNavigationEvent(`${oneIdPrefix}opt-in`);
		});

		["create", "reauth", "refresh", "low-trust"].forEach((event) => {
			disneyIdRef.current.on(event, () => {
				logProfileInfo(`${oneIdPrefix}${event}`);
				publishNavigationEvent(`${oneIdPrefix}${event}`);
			});
		});
	};

	const launchOneIdEvent = async (
		oneIdLibrary = {},
		method = "",
		argument = ""
	) => {
		if (oneIdLibrary) {
			try {
				await oneIdLibrary[method](argument);
				if (method?.indexOf("launch") === 0) {
					setOpenedOneIdModal(true);
				}
				return true;
			} catch (error) {
				if (useIdentityWebSDK && error?.message === "ALREADY_LOGGED_IN") {
					logProfileInfo("User already logged in, skipping login flow");
					return true;
				}
				console.error(error);
				return false;
			}
		} else {
			return false;
		}
	};

	const handleLogin = async (
		event,
		onLoginRedirectTo = null,
		onLogoutRedirectTo = null,
		preventReloadAndRedirect = false,
		preventExecuteOneIdMethod = false,
		forceEnablePageReloadAndRedirect = undefined
	) => {
		if (forceEnablePageReloadAndRedirect && onLoginRedirectTo) {
			if (!oneIdConfig.preventDefaultRedirectUrl) {
				disneyIdRef.current.onLoginRedirectTo = onLoginRedirectTo;
			} else {
				onLoginRedirectTo = "";
				disneyIdRef.current.onLoginRedirectTo = "";
				disneyIdRef.current.onLoginRedirectUrl = "";
			}
			disneyIdRef.current.forceEnablePageReloadAndRedirect = true;
			disneyIdRef.current.loginPreventReloadAndRedirect = false;
		}
		if (!oneIdConfig.preventAuthenticationModals) {
			if (isOneIdSource) {
				const loginMethod = useIdentityWebSDK
					? launchIdentityFlowMethod
					: launchLoginMethod;
				logProfileInfo("Login method selected", { loginMethod });
				handleClick(
					loginMethod,
					onLoginRedirectTo,
					onLogoutRedirectTo,
					preventReloadAndRedirect,
					preventExecuteOneIdMethod
				)(event);
			} else {
				event?.preventDefault();
				if (disneyIdRef.current) {
					try {
						if (
							await launchOneIdEvent(disneyIdRef.current, launchLoginMethod)
						) {
							setOpenedOneIdModal(false);
							return;
						}
					} catch (error) {
						console.error(error);
					}
				}
				window?.location.assign(
					addCurrentRedirectUrl(
						buildHref(
							chromeData?.signIn?.href || "/login/",
							store,
							basePath,
							false
						),
						true
					)
				);
			}
		}
	};

	const handleLogout = (
		event,
		onLogoutRedirectTo = null,
		isEventSource = false,
		preventReloadAndRedirect = false,
		preventExecuteOneIdMethod = false,
		forceEnablePageReloadAndRedirect = undefined
	) => {
		navigationAllowReloadAndRedirect = true;
		setSyndicatedDismissButton(false);
		setStorageMakeCartRequest(true);
		removeCartCookie();
		removeOciCookies();
		if (forceEnablePageReloadAndRedirect && onLogoutRedirectTo) {
			if (!oneIdConfig.preventDefaultRedirectUrl) {
				disneyIdRef.current.onLogoutRedirect = onLogoutRedirectTo;
			} else {
				onLogoutRedirectTo = "";
				disneyIdRef.current.onLogoutRedirectTo = "";
				disneyIdRef.current.onLogoutRedirectUrl = "";
			}
			disneyIdRef.current.forceEnablePageReloadAndRedirect = true;
			disneyIdRef.current.logoutPreventReloadAndRedirect = false;
		}
		if (
			disneyIdRef.current?.forceEnablePageReloadAndRedirect &&
			disneyIdRef.current?.onLogoutRedirectTo
		) {
			disneyIdRef.current.logoutPreventReloadAndRedirect = false;
		}
		if (isOneIdSource && !isTravelAgent(userType)) {
			if (!oneIdConfig.preventDefaultRedirectUrl) {
				disneyIdRef.current.onLogoutRedirectUrl = addCurrentRedirectUrl(
					onLogoutRedirectTo || chromeData?.signOut?.href,
					false
				);
			}
			handleClick(
				"logout",
				null,
				onLogoutRedirectTo,
				preventReloadAndRedirect,
				preventExecuteOneIdMethod
			)(event);
		} else if (isEventSource && onLogoutRedirectTo) {
			window?.location.assign(onLogoutRedirectTo);
		} else if (
			disneyIdRef.current?.onLogoutRedirectTo &&
			event?.currentTarget?.href
		) {
			event.currentTarget.href = disneyIdRef.current.onLogoutRedirectTo;
		} else if (window?.navigationKeepInFlow && event?.currentTarget?.href) {
			event.currentTarget.href = addCurrentRedirectUrl(
				event.currentTarget.href,
				true,
				true
			);
		}
	};

	const handleProfile = (
		event,
		onLoginRedirectTo = null,
		onLogoutRedirectTo = null,
		isEventSource = false
	) => {
		if (chromeData?.signedIn) {
			if (
				isOneIdSource &&
				(chromeData.signedIn?.target === config.targetOneId || bannerProfile)
			) {
				handleClick(
					"launchProfile",
					onLoginRedirectTo,
					onLogoutRedirectTo
				)(event);
			} else if (chromeData.signedIn?.href && isEventSource) {
				window?.location.assign(chromeData.signedIn.href);
			}
		}
	};

	const handleSubscription = async (event = null) => {
		if (!user && !disneyIdRef.current.leadGenLoggedIn) {
			disneyIdRef.current.leadGenLogin = true;
			handleLogin(event, null, null, true);
		} else {
			event?.preventDefault();
			try {
				const newsletterMethod = useIdentityWebSDK
					? "launchNewsLetterFlow"
					: "launchNewsletters";
				logProfileInfo("Newsletter method selected", { newsletterMethod });
				await launchOneIdEvent(
					disneyIdRef.current,
					newsletterMethod,
					oneIdConfig?.promotionId
				);
			} catch (error) {
				console.error(error);
			}
		}
	};

	useEffect(() => {
		if (triggerNotificationCount !== 0) {
			if (notificationsType === "heavy") {
				const storedNotificationsCount = { ...loadHeavyNotificationsObject };
				if (user?.cookieBasedNotifications) {
					if (notificationCount) {
						setLoadHeavyNotificationsObject({
							...storedNotificationsCount,
							itemCount: storedNotificationsCount.itemCount - 1,
							updatedAt: new Date().getTime(),
							outdated: false,
						});
						setNotificationCount(notificationCount - 1);
					}
				} else {
					setLoadHeavyNotificationsObject({
						...storedNotificationsCount,
						itemCount: storedNotificationsCount.itemCount - 1,
						outdated: true,
					});
				}
			}

			if (user?.messages?.itemCount > 0) {
				const updatedUser = JSON.parse(JSON.stringify(user));
				updatedUser.messages.itemCount--;
				setUser(updatedUser);
			}
		} // eslint-disable-next-line react-hooks/exhaustive-deps
	}, [triggerNotificationCount]);

	const handleReadNotification = () => {
		setTriggerNotificationCount(
			triggerNotificationCount + new Date().getTime()
		);
	};

	const handleClick = (
		method,
		onLoginRedirectTo = null,
		onLogoutRedirectTo = null,
		preventReloadAndRedirect = false,
		preventExecuteOneIdMethod = false,
		argument = null
	) => {
		return async (e) => {
			setAsyncMessagingToken(null);
			if (
				isLoading ||
				!isOneIdSource ||
				!disneyIdRef.current ||
				!disneyIdRef.current[method]
			)
				return;
			e?.preventDefault();
			if (onLoginRedirectTo) {
				disneyIdRef.current.onLoginRedirectTo = onLoginRedirectTo;
			}
			if (onLogoutRedirectTo) {
				disneyIdRef.current.onLogoutRedirectTo = onLogoutRedirectTo;
			}
			if (preventReloadAndRedirect) {
				disneyIdRef.current[`${method}PreventReloadAndRedirect`] =
					preventReloadAndRedirect;
			}
			if (!preventExecuteOneIdMethod) {
				launchOneIdEvent(disneyIdRef.current, method, argument);
			}
		};
	};

	return {
		isLoading,
		user,
		isOneIdSource,
		setUser,
		userType,
		setUserType,
		travelAgent,
		setTravelAgent,
		handleClick,
		openedOneIdModal,
		affiliations,
		handleLogin,
		handleLogout,
		handleProfile,
		handleSubscription,
		showSubscriptionMessage,
	};
};

export default useProfile;
